<template>
	<div class="email-wrapper">
		<div
			class="email-login"
			v-if="!signedIn"
		>
			<h1>{{ props.content.componentTitle || "Logg inn på Online e-post" }}</h1>
			<TnButton
				icon-left="telenor"
				:primary="true"
				:dark="true"
				class="margin-top-3xl"
				id="telenor-id-btn"
				@click="() => telenorIdSignIn()"
			>
				{{ props.content.loginWithTelenorIdText || "Logg inn med Telenor ID" }}
			</TnButton>
			<div class="seperator margin-top-3xl">
				<span>{{ props.content.separatorText || "eller med" }}</span>
			</div>
			<form
				id="loginemailform"
				class="login-email-form"
				method="POST"
				:action="runtimeConfig.public.hostopia.formLoginUrl"
				@submit.prevent.stop="handleSubmit"
				ref="loginForm"
			>
				<input
					type="hidden"
					name="CustomError"
					:value="EpostConstants.EPOST.LOGIN_URL + '?loginError=1'"
				/>
				<input
					type="hidden"
					name="CustomLogin"
					:value="EpostConstants.EPOST.LOGIN_URL"
				/>
				<TnTextField
					v-model="username"
					:dark="true"
					size="m"
					type="email"
					label="E-postadresse:"
					name="user"
					placeholder="dinepost@online.no"
					id="email-username"
					class="margin-top-xl"
					:clearable="true"
					required
					aria-label="E-postadresse"
				>
				</TnTextField>

				<TnTextField
					v-model="password"
					:dark="true"
					size="m"
					type="password"
					label="Passord:"
					name="password"
					placeholder="Ditt online passord"
					id="email-password"
					class="margin-top-m"
					:clearable="true"
					required
					aria-label="E-post passord"
				/>

				<div class="margin-top-l bottom-cta-group">
					<TnButton
						:primary="true"
						:dark="true"
						:text="props.content.loginButtonText || 'Logg inn'"
						type="submit"
					/>
					<TnLink
						class="margin-top-s"
						:dark="true"
						:text="props.content.forgotPasswordText || 'Glemt passord?'"
						to="/privat/kundeservice/epost/innlogging/"
					/>
				</div>
				<TnNotification
					v-if="errorMessage"
					type="error"
					>{{ errorMessage }}</TnNotification
				>
			</form>
		</div>

		<div
			class="email-logout"
			v-else-if="signedIn"
		>
			<h1>{{ props.content.loggedInTitle || "Online e-post" }}</h1>
			<TnParagraph
				class="margin-top-m"
				:dark="true"
				size="s"
			>
				{{
					props.content.loggedInDescription ||
					"Du er allerede logget inn med din Telenor ID, så om du ønsker å gå til Online e-post trykker du bare på knappen nedenfor. Derfra går du bare gjennom et lite steg til før du er inne på din e-postkonto."
				}}
			</TnParagraph>
			<TnButton
				:primary="true"
				:dark="true"
				class="margin-top-3xl"
				@click="goToEpost"
			>
				{{ props.content.goToEpostText || "Gå til Online e-post" }}
			</TnButton>
			<div class="seperator margin-top-3xl">
				<span>{{ props.content.separatorText || "eller" }}</span>
			</div>
			<TnButton
				:secondary="true"
				:dark="true"
				class="margin-top-3xl"
				@click="() => telenorIdSignOut()"
			>
				{{ props.content.logoutText || "Logg ut" }}
			</TnButton>
		</div>

		<div
			class="spinner-container"
			v-else
		>
			<TnSpinner />
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Schema } from "./epost-login.js";
import EpostConstants from "../common/epostConstants";
import mgr from "~/telenorid/telenorid";

const props = defineProps<Schema>();
const runtimeConfig = useRuntimeConfig();
const loginForm = useTemplateRef("loginForm");

const username = defineModel<string>("username", { default: "" });
const password = defineModel<string>("password", { default: "" });
const errorMessage = ref<string>("");

const router = useRouter();
const goToEpost = () => {
	const currentPath = router.currentRoute.value.path.replace(/\/$/, "");
	router.push(`${currentPath}/epost`);
};
const signedIn = ref<null | boolean>(null);

const initializeSession = async () => {
	try {
		const user = await mgr.getOrLoginUser();
		signedIn.value = !!user;
	} catch (err) {
		console.error("Error during session initialization:", err);
		signedIn.value = false;
	}
};

onMounted(async () => {
	await initializeSession();
});

const handleSubmit = async (event: SubmitEvent) => {
	event.preventDefault();

	try {
		const payload = {
			user: username.value,
			password: password.value,
		};

		const success = await $fetch<boolean>(EpostConstants.EPOST.PROXY_URL, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(payload),
		});

		if (success) {
			loginForm.value?.submit();
		} else {
			errorMessage.value = props.content.errorSubmissionFailed || "Feil i innsending av skjema.";
		}
	} catch (err: any) {
		if (err.statusCode === 401) {
			errorMessage.value = props.content.errorInvalidCredentials || "Feil e-post og/eller passord.";
		} else if (err.statusCode === 400) {
			errorMessage.value =
				props.content.errorMissingFields ||
				"Manglende eller ugyldige felter i skjemaet. Sjekk at alle feltene er fylt ut.";
		} else if (err.statusCode === 502) {
			errorMessage.value =
				props.content.errorServiceUnavailable || "Tjenesten er midlertidig utilgjengelig. Vennligst prøv igjen senere.";
		} else if (err.statusCode === 500) {
			errorMessage.value = props.content.errorInternal || "En intern feil oppstod. Vennligst prøv igjen senere.";
		} else {
			errorMessage.value = props.content.errorUnknown || "En ukjent feil oppstod. Vennligst prøv igjen.";
		}

		if (process.env.NODE_ENV !== "production") {
			console.error("Error during login:", err);
		}
	}
};

const telenorIdSignIn = async (callbackUrl = "/online/epost") => {
	try {
		await mgr.loginRedirectBackHere({ from: "online" }, false, callbackUrl);
	} catch (err) {
		console.error("Error during Telenor ID login:", err);
	}
};

const telenorIdSignOut = async (callbackUrl = "/online") => {
	try {
		await mgr.setCallbackRedirectUrl(callbackUrl);
		await mgr.signoutRedirect();
	} catch (err) {
		console.error("Error during Telenor ID logout:", err);
	}
};
</script>

<style lang="scss" scoped>
::v-deep(#telenor-id-btn svg g#Telenor path) {
	fill: inherit !important;
}

.seperator {
	display: flex;
	align-items: center;
	text-align: center;

	&::before,
	&::after {
		content: "";
		flex: 1;
		height: 1px;
		background-color: #c5cbdd;
	}

	span {
		margin: 0 48px;
		font-size: 16px;
	}
}

.email-wrapper {
	width: 100%;
	padding: 70px 32px;
	background: var(--Surface-Inverted, #000b2e);
	color: #fff;
	display: flex;
	justify-content: center;
	align-items: center;
}

.spinner-container {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 460px;
	width: 100%;
}

h1 {
	@include font-title-s;
}

.email-login,
.email-logout {
	width: 100%;
	max-width: 500px;
	display: flex;
	flex-direction: column;
}

.email-logout {
	padding-top: 46px;
}

.email-login {
	.login-email-form {
		display: flex;
		flex-direction: column;
	}

	.bottom-cta-group {
		display: flex;
		flex-direction: column;
	}
}
</style>
